// Decoration utilities for dealing with adding classnames to decorated
// elements. We want the decorations to be added at the top level of the
// decorations object because slate's memoization only does a shallow
// comparison of the entries within the object.
import { Editor, Text, Range } from 'slate';

import {
  type ClassNameDecoration,
  type DecorateNodeCallback,
  type Decorations as DecorationsObject,
} from '../types/aha-editor';

class Decorations {
  static CLASS_PREFIX = 'decorationClass__' as const;

  static decorationClass(className: string): ClassNameDecoration {
    return `${this.CLASS_PREFIX}${className}`;
  }

  static decorateTextNodesInRange(
    editor: Editor,
    range: Range,
    decorations: DecorationsObject,
    decorateNode: DecorateNodeCallback
  ): void {
    const nodeEntries = Array.from(
      Editor.nodes(editor, {
        at: range,
        reverse: Range.isBackward(range),
        match: Text.isText,
        mode: 'lowest',
      })
    );

    for (const [node, path] of nodeEntries) {
      const textNodeRange = {
        anchor: { path, offset: 0 },
        focus: { path, offset: node.text.length },
      };
      const decorationRange = Range.intersection(range, textNodeRange);
      if (!decorationRange) continue;

      decorateNode(node, decorationRange, decorations);
    }
  }

  private static isDecorationClass(
    decoration: string
  ): decoration is ClassNameDecoration {
    return decoration.startsWith(this.CLASS_PREFIX);
  }

  private static className(decorationClass: string): string {
    return decorationClass.substring(this.CLASS_PREFIX.length);
  }

  static extractDecorationClasses(
    decorations: DecorationsObject['classNames']
  ) {
    const decorationClasses: Record<string, boolean> = {};
    for (const key in decorations) {
      if (!this.isDecorationClass(key)) continue;

      const isActive = decorations[key];
      const className = this.className(key);
      decorationClasses[className] = isActive;
    }
    return decorationClasses;
  }
}

export default Decorations;
